"use client";

import FallbackImage from "@/components/custom/fallback-image";
import { CustomIcon } from "@/components/custom/icons/custom-icon";
import { Typography } from "@/components/custom/typography";
import { Checkbox } from "@/components/ui/checkbox";
import { USER_TYPE } from "@/lib/constants";
import { cn, formatPrice, groupDataByKey } from "@/lib/utils";
import Link from "next/link";
import { useMapUIStore } from "@/stores/mapUIStore";
import { useUser } from "@/stores/user";
import { MediaInfoList } from "./MediaInfoList";
import { useMediaCardState } from "@/hooks/map-hooks/useMediaCardState";
import { MediaProduct } from "@/services/graphql/generated";
import { useMemo } from "react";
import { useMediaCategories } from "@/hooks/useMediaCategories";
import { ChevronRightIcon } from "lucide-react";
import { useCommonCDByCodes } from "@/hooks/useCommonCD";
import { toSlug } from "@/lib/utils/to-slug";
import { getCookieInClientSide } from "@/lib/utils/get-cookies-for-client";
import { useSearchParams } from "next/navigation";
import { useTypedTranslations } from "@/i18n/typed";
import type { Keys_mediaCategory } from "@/i18n/generated/keys.generated";

export interface MediaHomeCardProps {
  data: MediaProduct;
  activeIds?: Set<number>;
  onRequireLogin?: () => void;
  onBookmarkSuccess?: () => void;
}

export default function MediaHomeCard({
  data,
  activeIds,
  onRequireLogin,
  onBookmarkSuccess,
}: MediaHomeCardProps) {
  const t = useTypedTranslations("mediaOnMap");
  const tMediaCategory = useTypedTranslations("mediaCategory");
  const tUnit = useTypedTranslations("unit");
  const locale = getCookieInClientSide("NEXT_LOCALE");
  const searchParams = useSearchParams();
  const queryString = searchParams?.toString();

  const setHoveredPin = useMapUIStore((s) => s.setHoveredPin);
  const setCheckedPin = useMapUIStore((s) => s.setCheckedPin);
  const focusPoiFromCard = useMapUIStore((s) => s.focusPoiFromCard);
  const checkedPins = useMapUIStore((s) => s.checkedPins);
  const zoom = useMapUIStore((s) => s.zoom);

  const { userInfo } = useUser();
  const { data: mediaCategoryDataRes } = useMediaCategories();
  const { data: mediaCategoryData } = mediaCategoryDataRes || {};
  const { data: commonCDDataRes } = useCommonCDByCodes(["media_install_site"]);
  const { data: commonCDData } = commonCDDataRes || {};
  const isMedia = userInfo?.user?.user_type?.toLowerCase() === USER_TYPE.media;
  const isAdvertiser = userInfo?.user?.user_type?.toLowerCase() === USER_TYPE.advertiser;

  const commonCDDataGroup = useMemo(() => {
    if (!commonCDData) return null;
    return groupDataByKey(commonCDData, "grp_sub_cd");
  }, [commonCDData]);

  const mediaCategoryDataGroup = useMemo(() => {
    if (!mediaCategoryData) return null;
    return groupDataByKey(mediaCategoryData, "id");
  }, [mediaCategoryData]);

  const category = mediaCategoryDataGroup?.[data?.mediaCategoryId || ""]?.[0];
  const adFeePerMonth = data?.adServiceInfos?.[0].adServiceOptions?.find(opt => opt.isDefaultOption)?.adFeePerMonth;
  const mediaInfo = data?.mediaGroups?.mediaGroups?.[0]?.medias?.medias?.[0]?.mediaInfo ?? { lat: 0, lng: 0 };

  const { optimisticBookmark, isPending, toggleBookmark } = useMediaCardState({
    data,
    userInfo,
    onRequireLogin,
    onBookmarkSuccess,
  });

  return (
    <div
      className={cn(
        "flex items-center justify-between p-3 border-b cursor-pointer",
        activeIds?.has(data.id)
          ? "border-l-4 border-l-primary-500 "
          : "border-gray-200",
        isPending && "opacity-70",
      )}
      onMouseEnter={() => setHoveredPin(data.id.toString())}
      onMouseLeave={() => setHoveredPin(null)}
      onClick={() =>
        focusPoiFromCard({
          id: data.id,
          lat: mediaInfo.lat,
          lng: mediaInfo.lng,
          mediaGroupPoint: `${mediaInfo.lat},${mediaInfo.lng}`,
        })
      }
    >
      {/* Left Image */}
      <div className="relative size-[130px] flex-shrink-0">
        <FallbackImage
          src={data.bannerUrl || ""}
          alt={t("fallback.media")}
          fill
          className="object-cover"
        />

        {!isMedia && (
          <button
            onClick={toggleBookmark}
            disabled={isPending}
            className={cn(
              "absolute top-2 left-2 w-6 h-6 flex items-center justify-center bg-white rounded shadow z-10 transition cursor-pointer",
              optimisticBookmark && "bg-primary-200",
            )}
          >
            <CustomIcon
              name="Bookmark"
              size={4}
              className={cn(
                optimisticBookmark ? "text-primary" : "text-muted-foreground",
                isPending && "opacity-50",
              )}
            />
          </button>
        )}
      </div>

      {/* Right Content */}
      <div className="flex flex-col flex-1 ml-3 gap-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center ">
              <Typography variant={"detail2"} tone="description">
                {category?.parent_cd
                  ? commonCDDataGroup?.[category?.parent_cd]?.[0]
                    ?.common_cd_name
                  : t("common.unknown")}
              </Typography>{" "}
              <ChevronRightIcon className="size-3" />{" "}
              <Typography variant={"detail2"} tone="description">
                {(category?.id &&
                  tMediaCategory(String(category.id) as Keys_mediaCategory)) ||
                  t("common.unknown")}
              </Typography>
            </div>

            <Link
              href={`/media/${toSlug(data.name || "", locale)}/${data.id}/@${mediaInfo.lat
                },${mediaInfo.lng},${zoom}z?id=${data.id}${queryString ? `&${queryString}` : ""
                }`}
              className="text-primary-500 font-semibold text-sm line-clamp-1"
              onClick={(e) => e.stopPropagation()}
            >
              {data.name}
            </Link>
          </div>

          {!isAdvertiser && (
            <Checkbox
              checked={checkedPins.has(data.id.toString())}
              onCheckedChange={(checked) =>
                setCheckedPin(data.id.toString(), checked === true)
              }
              onClick={(e) => e.stopPropagation()}
              className="cursor-pointer"
            />
          )}
        </div>

        <div>
          <MediaInfoList data={data} />
        </div>

        <Typography variant="body2" className="pt-1 font-semibold text-black">
          {tUnit("currency.krw", {
            amount: formatPrice(adFeePerMonth || 0),
          })}{" "}
          | {t("price.perMonth")}
        </Typography>
      </div>
    </div>
  );
}
